// hooks/useApi.ts
import { useState, useCallback } from "react";
import { notify } from "../components/ui/Toast";
import { useAuthenticatedFetch } from "./useAuthenticatedFetch";
import { showError, toAppError } from "../lib/feedback";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TIPOS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export type ValidationError = {
  field: string;    // ex: "email", "address → street"
  message: string;  // ex: "field required"
  type: string;     // ex: "missing", "value_error"
};

export type ApiError = {
  status: number;               // HTTP status: 400, 404, 422, 500, 502...
  type: ErrorType;              // código interno do erro
  title: string;                // título legível
  detail: string;               // mensagem segura para mostrar ao usuário
  requestId?: string;           // ID para correlacionar com logs do servidor
  errors?: ValidationError[];   // só existe em erros 422
  retryable?: boolean;          // false = não repetir (POST/PUT/DELETE: repetir duplicaria a ação)
};

// Um erro do app, venha do backend (RFC 7807) ou do próprio front.
// A mensagem para o usuário vem de src/i18n/errors.pt-BR.ts.
export type ErrorType = BackendErrorType | ClientErrorType;

// Erros que só o front produz (rede, tempo, falha do próprio app, validação de arquivo/permissão).
export type ClientErrorType =
  | "network-error"        // sem internet (navigator.onLine === false)
  | "server-unreachable"   // com internet, mas o servidor não respondeu (ou respondeu sem CORS)
  | "timeout"
  | "stream-interrupted"   // a resposta da IA veio vazia ou foi cortada
  | "unexpected"           // bug do front: exceção de JS, erro de render
  | "file-type-not-allowed"
  | "file-too-large"
  | "file-duplicated"
  | "mic-permission-denied"
  | "clipboard-failed"
  | "unknown";             // o servidor respondeu algo que não é RFC 7807 (HTML do gateway etc.)

// Espelha ErrorCodes do backend (vytalia-mvp-chatbot/exceptions.py).
export type BackendErrorType =
  // Genéricos
  | "internal-server-error"
  | "invalid-payload"
  | "not-found"
  | "unauthorized"
  | "forbidden"
  | "conflict"
  // Domínio LetsDoc
  | "pdf-generation-failed"
  | "s3-upload-failed"
  | "s3-delete-failed"
  | "s3-list-failed"
  | "ocr-failed"
  | "rag-chain-not-found"
  | "dynamodb-write-failed"
  | "dynamodb-update-failed"
  | "dynamodb-read-failed"
  | "dynamodb-delete-failed"
  | "stripe-error"
  | "stripe-invalid-payload"
  | "stripe-invalid-signature"
  | "plan-update-failed"
  | "openai-error"
  | "gemini-error"
  | "service-busy"
  | "db-update-failed"
  | "db-write-failed"
  | "db-connection-failed"
  | "email-account-not-connected"
  | "email-oauth-failed"
  | "email-send-failed"
  // Domínio Billing/Onboarding
  | "plano-desconhecido"
  | "lookup-key-desconhecida"
  | "secretaria-sem-medico"
  | "papel-sem-permissao"
  | "assinatura-ja-existe"
  | "plano-inativo"
  | "limite-free-atingido"
  | "prazo-arrependimento-expirado"
  | "convite-invalido"
  | "convite-email-diferente"
  | "pagamento-nao-vinculavel"
  | "oferta-indisponivel"
  | "oferta-ja-utilizada"
  | "plano-avulso-ativo"
  | "ja-em-outra-clinica"
  | "db-not-found"
  | "pagamento-recusado";

type ApiState<T> = {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
};

export type AuthFetch = <T>(path: string, options?: RequestInit) => Promise<T>;
export type PublicFetch = <T>(path: string, options?: RequestInit) => Promise<T>;
export type AuthFetchForm = <T>(path: string, formData: FormData, options?: Omit<RequestInit, "body">) => Promise<T>;
export type AuthFetchBlob = (path: string, options?: RequestInit) => Promise<Blob>;
export type AuthFetchRaw = (path: string, options?: RequestInit) => Promise<Response>;


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// PARSER DE RESPOSTA DE ERRO
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// Só GET é seguro de repetir: repetir um POST que deu 5xx pode duplicar a
// ação (checkout, convite, pedido) e faz o usuário esperar 3× pelo erro.
function podeRepetir(method?: string): boolean {
  return (method ?? "GET").toUpperCase() === "GET";
}

async function parseApiError(response: Response, method?: string): Promise<ApiError> {
  const retryable = podeRepetir(method);
  try {
    const body = await response.json();
    // O backend retorna: { type: "https://errors.letsdoc.ai/not-found", ... }
    // Pegamos só a última parte da URL como tipo
    const rawType = body.type?.split("/").pop() ?? "unknown";
    // `message` é a frase para o usuário; `detail` fica só no log do backend.
    // O fallback para `detail` cobre um backend antigo ainda no ar.
    const userText = body.message ?? body.detail;
    return {
      status:    response.status,
      type:      rawType as ErrorType,
      title:     body.title ?? "",
      detail:    typeof userText === "string" ? userText : "",
      requestId: body.request_id,
      errors:    body.errors,
      retryable,
    };
  } catch {
    // Não é JSON (HTML do load balancer, 502/504 do gateway...): a frase vem do catálogo.
    return { status: response.status, type: "unknown", title: "", detail: "", retryable };
  }
}

// fetch() lançou: sem internet, servidor fora do ar/sem CORS, ou estourou o tempo.
function networkError(err: unknown, method?: string): ApiError {
  const type: ErrorType =
    err instanceof DOMException && err.name === "TimeoutError" ? "timeout"
    : typeof navigator !== "undefined" && !navigator.onLine ? "network-error"
    : "server-unreachable";
  return { status: 0, type, title: "", detail: "", retryable: podeRepetir(method) };
}

// Sem resposta em 60s, o fetch aborta e vira "timeout". Quem passa `signal` controla o próprio tempo.
const TIMEOUT_MS = 60_000;
const withTimeout = (signal?: AbortSignal | null) => signal ?? AbortSignal.timeout?.(TIMEOUT_MS);


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// RETRY — não retenta erros 4xx (são erros do cliente, retentar não adianta)
// Retenta erros 5xx e de rede com backoff exponencial
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

async function withRetry<T>(
  fn: () => Promise<T>,
  retries = 2,
  delayMs = 500
): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    const { status = 0, retryable } = err as { status?: number; retryable?: boolean };
    const isClientError = status >= 400 && status < 500;

    // 4xx: erro do cliente — retentar não vai resolver
    if (isClientError) throw err;

    // POST/PUT/DELETE: repetir pode duplicar a ação
    if (retryable === false) throw err;

    // Sem mais tentativas
    if (retries === 0) throw err;

    // 5xx ou sem rede: aguarda e tenta de novo
    await new Promise((r) => setTimeout(r, delayMs));
    return withRetry(fn, retries - 1, delayMs * 2); // backoff exponencial: 500ms → 1000ms → 2000ms
  }
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// apiFetch — fetch tipado que lança ApiError em caso de falha
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const BASE_URL = process.env.REACT_APP_API_ENDPOINT;

async function _apiFetch<T>(
  path: string,
  options?: RequestInit
): Promise<T> {

  const { headers: customHeaders, ...restOptions } = options ?? {};
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      headers: {
        "Content-Type": "application/json",
        ...customHeaders,
      },
      ...restOptions,
      signal: withTimeout(restOptions.signal),
    });
  } catch (err) {
    throw networkError(err, restOptions.method);
  }

  if (!response.ok) {
    throw await parseApiError(response, restOptions.method);
  }

  // 204 No Content — sem body para parsear
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// Versão para upload de arquivos (sem Content-Type — o browser define o boundary)
async function _apiFetchForm<T>(
  path: string,
  formData: FormData,
  options?: Omit<RequestInit, "body">
): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: "POST",
      body: formData,
      ...options,
      signal: withTimeout(options?.signal),
    });
  } catch (err) {
    throw networkError(err, "POST");
  }

  if (!response.ok) {
    throw await parseApiError(response, "POST");
  }

  return response.json() as Promise<T>;
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// apiFetchBlob — fetch para endpoints que retornam arquivo binário (PDF, etc.)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

async function _apiFetchBlob(
  path: string,
  options?: RequestInit
): Promise<Blob> {
  const { headers: customHeaders, ...restOptions } = options ?? {};
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      headers: {
        "Content-Type": "application/json",
        ...customHeaders,
      },
      ...restOptions,
    });
  } catch (err) {
    throw networkError(err, restOptions.method);
  }

  if (!response.ok) {
    throw await parseApiError(response, restOptions.method);
  }

  return response.blob();
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// apiFetchRaw — fetch autenticado que retorna a Response bruta (para streaming)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

async function _apiFetchRaw(
  path: string,
  options?: RequestInit
): Promise<Response> {
  const { headers: customHeaders, ...restOptions } = options ?? {};
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      headers: {
        "Content-Type": "application/json",
        ...customHeaders,
      },
      ...restOptions,
    });
  } catch (err) {
    throw networkError(err, restOptions.method);
  }

  // Sem isso o JSON do erro ia parar no texto do pedido.
  if (!response.ok) {
    throw await parseApiError(response, restOptions.method);
  }

  return response;
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// publicFetch — mesmo _apiFetch (erro RFC7807 + retry), sem Authorization.
// Único uso hoje: GET /billing/convites/{token} — precisa funcionar antes
// do usuário logar. Não é hook (nada de token/getAccessTokenSilently), por
// isso pode ser chamado direto de um service, fora de componente.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const publicFetch: PublicFetch = _apiFetch;


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// useApi — hook principal
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function useApi<T = unknown>() {
  const { getToken } = useAuthenticatedFetch();

  const [state, setState] = useState<ApiState<T>>({
    data:    null,
    loading: false,
    error:   null,
  });

  const apiFetch: AuthFetch = useCallback(async <R>(path: string, options?: RequestInit): Promise<R> => {
    const token = await getToken();
    return _apiFetch<R>(path, {
      ...options,
      headers: { ...options?.headers, Authorization: `Bearer ${token}` },
    });
  }, [getToken]) as AuthFetch;

  const apiFetchForm: AuthFetchForm = useCallback(async <R>(path: string, formData: FormData, options?: Omit<RequestInit, "body">): Promise<R> => {
    const token = await getToken();
    return _apiFetchForm<R>(path, formData, {
      ...options,
      headers: { ...options?.headers, Authorization: `Bearer ${token}` },
    });
  }, [getToken]) as AuthFetchForm;

  const apiFetchBlob: AuthFetchBlob = useCallback(async (path: string, options?: RequestInit): Promise<Blob> => {
    const token = await getToken();
    return _apiFetchBlob(path, {
      ...options,
      headers: { ...options?.headers, Authorization: `Bearer ${token}` },
    });
  }, [getToken]);

  const apiFetchRaw: AuthFetchRaw = useCallback(async (path: string, options?: RequestInit): Promise<Response> => {
    const token = await getToken();
    return _apiFetchRaw(path, {
      ...options,
      headers: { ...options?.headers, Authorization: `Bearer ${token}` },
    });
  }, [getToken]);

  const request = useCallback(async (
    fn: () => Promise<T>,
    options?: {
      successMessage?: string;  // se passado, mostra o toast de sucesso ao completar
      showErrorToast?: boolean; // default: true — passa false para tratar o erro manualmente
    }
  ): Promise<T | null> => {
    const { successMessage, showErrorToast: showToastOnError = true } = options ?? {};

    setState({ data: null, loading: true, error: null });

    try {
      const data = await withRetry(fn);
      setState({ data, loading: false, error: null });

      if (successMessage) {
        notify({ tone: "success", title: successMessage });
      }

      return data;

    } catch (err) {
      const apiError = toAppError(err);
      setState({ data: null, loading: false, error: apiError });

      if (showToastOnError) {
        showError(apiError, { retry: () => void request(fn, options) });
      }

      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, request, reset, apiFetch, apiFetchForm, apiFetchBlob, apiFetchRaw };
}